import { createAuthClient } from 'better-auth/react';
import { resolveApiUrl } from './api-url';

export const authClient = createAuthClient({
  baseURL: resolveApiUrl(),
  fetchOptions: {
    credentials: 'include',
  },
});

export const { signIn, signUp, signOut } = authClient;

/** Request a password-reset email via Better Auth. */
export async function forgetPassword(params: { email: string; redirectTo: string }) {
  return authClient.$fetch('/request-password-reset', {
    method: 'POST',
    body: params,
    credentials: 'include',
  });
}

/** Re-send the verification email for the current user. */
export async function resendVerificationEmail(email: string, callbackURL: string) {
  return authClient.$fetch('/send-verification-email', {
    method: 'POST',
    body: { email, callbackURL },
    credentials: 'include',
  });
}

/** Complete a password reset with the token from the email link. */
export async function resetPassword(params: { token: string; newPassword: string }) {
  return authClient.$fetch('/reset-password', {
    method: 'POST',
    body: params,
    credentials: 'include',
  });
}

/**
 * Verify a Scout-onboarding magic-link token. Establishes the cookie
 * session (creating the account if the email is new). Deliberately called
 * only from the device-signin page's button press — never on page load —
 * so mail scanners that prefetch the emailed URL can't consume the token.
 */
export async function verifyMagicLink(token: string) {
  return authClient.$fetch(`/magic-link/verify?token=${encodeURIComponent(token)}`, {
    method: 'GET',
    credentials: 'include',
  });
}

/**
 * Change the authenticated user's email address. Throws on a refusal: the
 * client hands errors back instead of throwing, and the Settings page's
 * success toast used to show for a refused change.
 */
export async function changeEmail(params: { newEmail: string; callbackURL?: string }) {
  const res = await authClient.$fetch('/change-email', {
    method: 'POST',
    body: params,
    credentials: 'include',
  });
  const error = (res as { error?: { message?: string } | null } | null)?.error;
  if (error) throw new Error(error.message || 'Could not change email.');
  return res;
}

/**
 * Spend the one-time link a Scout's web view opened (/auth/scout): signs the
 * Scout's owner in unless someone is already signed in here
 * (docs/core/architecture/SCOUT_MYPOLARIS_SIGN_IN.md).
 */
export async function scoutSignIn(token: string) {
  return authClient.$fetch('/scout/sign-in', {
    method: 'POST',
    body: { token },
    credentials: 'include',
  });
}

/** Change the authenticated user's password. */
export async function changePassword(params: {
  currentPassword: string;
  newPassword: string;
  revokeOtherSessions?: boolean;
}) {
  return authClient.$fetch('/change-password', {
    method: 'POST',
    body: params,
    credentials: 'include',
  });
}
